import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { shortDay } from '@/lib/format';
import { stopMark } from './artifact-meta';
import { Fact, FactLines, StatusPill, caption } from './parts';

type Load = Extract<WireArtifact, { kind: 'load' }>;
type Stop = Load['stops'][number];

/** The design's load card: reference and status, pickup → delivery, then the facts two by two. */
export function LoadArtifact({ artifact }: { artifact: Load }): JSX.Element {
  const pickup = artifact.stops.find((s) => s.kind === 'pickup') ?? artifact.stops[0];
  const delivery =
    [...artifact.stops].reverse().find((s) => s.kind === 'delivery') ?? artifact.stops.at(-1);
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 font-mono text-xl font-medium tracking-[-.01em] [overflow-wrap:anywhere]">
          #{artifact.reference.replace(/^#/, '')}
        </span>
        {artifact.status !== undefined && <StatusPill status={artifact.status} />}
      </div>
      {(pickup !== undefined || delivery !== undefined) && (
        <div className="border-line bg-surf grid grid-cols-[1fr_auto_1fr] items-center gap-2.5 rounded-xl border p-3.5">
          <StopCell label="PICKUP" stop={pickup} />
          <div className="text-acc text-lg">→</div>
          <StopCell label="DELIVERY" stop={delivery === pickup ? undefined : delivery} end />
        </div>
      )}
      {artifact.stops.some((s) => s.facts.length > 0) && (
        <div className="flex flex-col gap-2.5">
          {artifact.stops
            .filter((s) => s.facts.length > 0)
            .map((s, i) => (
              <StopFacts key={i} stop={s} />
            ))}
        </div>
      )}
      {artifact.facts.length > 0 && (
        <div className="grid grid-cols-2 gap-2.5">
          {artifact.facts.map((f, i) => (
            <Fact key={i} k={f.k} v={f.v} />
          ))}
        </div>
      )}
    </div>
  );
}

function StopCell({
  label,
  stop,
  end = false,
}: {
  label: string;
  stop: Stop | undefined;
  end?: boolean;
}): JSX.Element {
  return (
    <div className={end ? 'min-w-0 text-right' : 'min-w-0'}>
      <div className={caption}>{label}</div>
      <div className="mt-[3px] text-sm font-medium [overflow-wrap:anywhere]">
        {stop?.place ?? '—'}
      </div>
      {stop?.day !== undefined && (
        <div className="font-mono text-[11px] tabular-nums">{shortDay(stop.day)}</div>
      )}
      {stop?.at !== undefined && <div className="text-mute text-xs">{stop.at}</div>}
    </div>
  );
}

/** What the mapper had for one stop, under the leg it belongs to. */
function StopFacts({ stop }: { stop: Stop }): JSX.Element {
  return (
    <div className="border-line bg-surf rounded-[10px] border px-3.5 py-3">
      <div className="flex items-baseline gap-2">
        <span className="text-acc flex-none font-mono text-[10px] tracking-[.08em]">
          {stopMark(stop.kind)}
        </span>
        <span className="min-w-0 truncate text-[12.5px] font-medium">{stop.place}</span>
      </div>
      <FactLines facts={stop.facts} />
    </div>
  );
}
